import { Button } from '@tempo-io/sui-button';
import { Calendar } from '@tempo-io/sui-calendar';
import { CalendarToday, Close } from '@tempo-io/sui-icons';
import { Popover, PopoverContent, PopoverTrigger } from '@tempo-io/sui-popover';
import { formatISO, isAfter, isBefore, parseISO } from 'date-fns';
import React from 'react';
import type { ControllerRenderProps, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatLocalDate } from '@/lib/utils/common/dateFormats';

interface DatePickerFieldBaseProps<
  TFormValues extends FieldValues,
  TFieldName extends Path<TFormValues>,
> {
  field: ControllerRenderProps<TFormValues, TFieldName>;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string;
}

type ClearableDatePickerFieldProps<
  TFormValues extends FieldValues,
  TFieldName extends Path<TFormValues>,
> = DatePickerFieldBaseProps<TFormValues, TFieldName> & {
  isClearable: true;
  clearValue: TFormValues[TFieldName];
};

type NonClearableDatePickerFieldProps<
  TFormValues extends FieldValues,
  TFieldName extends Path<TFormValues>,
> = DatePickerFieldBaseProps<TFormValues, TFieldName> & {
  isClearable?: false;
  clearValue?: never;
};

type DatePickerFieldProps<TFormValues extends FieldValues, TFieldName extends Path<TFormValues>> =
  | ClearableDatePickerFieldProps<TFormValues, TFieldName>
  | NonClearableDatePickerFieldProps<TFormValues, TFieldName>;

export const DatePickerField = <
  TFormValues extends FieldValues,
  TFieldName extends Path<TFormValues>,
>({
  field,
  disabled = false,
  minDate,
  maxDate,
  placeholder,
  isClearable = false,
  clearValue,
}: DatePickerFieldProps<TFormValues, TFieldName>) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const hasValue = typeof field.value === 'string' && field.value.length > 0;
  const selectedDate = hasValue ? parseISO(field.value) : null;
  const formattedDate = selectedDate
    ? formatLocalDate(selectedDate.toISOString())
    : (placeholder ?? t(($) => $.adaptivePlanner.itemPanel.customFields.dateField.selectDate));

  const buttonId = `${field.name}-button`;

  const isDateDisabled = (date: Date) => {
    if (minDate && isBefore(date, minDate)) return true;
    if (maxDate && isAfter(date, maxDate)) return true;
    return false;
  };

  return (
    <div className="relative">
      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={buttonId}
            variant="outline"
            className="h-10 w-full justify-start rounded-md border bg-bg-surface-base px-3 py-2 pr-10 text-left font-normal shadow-sm"
            disabled={disabled}
          >
            <CalendarToday />
            {formattedDate}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          aria-labelledby={`${field.name}-label`}
          aria-describedby={`${field.name}-description`}
        >
          <Calendar
            mode="single"
            defaultMonth={selectedDate ?? new Date()}
            selected={selectedDate ?? undefined}
            onSelect={(date) => {
              if (date) {
                field.onChange(formatISO(date, { representation: 'date' }));
                setOpen(false);
              }
            }}
            disabled={isDateDisabled}
          />
        </PopoverContent>
      </Popover>
      {isClearable && hasValue && !disabled && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute right-1 top-1/2 z-10 h-8 w-8 -translate-y-1/2 p-0 text-text-neutral-secondary hover:text-text-neutral-primary"
          aria-label={t(($) => $.common.clearDate)}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            field.onChange(clearValue);
            setOpen(false);
          }}
        >
          <Close className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};
